import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { login as apiLogin, getCurrentUser } from '../services/api';

// Define the shape of the context
interface AuthContextType {
  authState: AuthState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initial auth state
const initialAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
};

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const user = await getCurrentUser();
          
          // Check if user has 'master' role
          if (user.cargo !== 'master') {
            localStorage.removeItem('token');
            setAuthState({
              ...initialAuthState,
              loading: false,
              error: 'Usuário não tem privilégios suficientes para acessar o sistema. Consulte o administrador',
            });
            return;
          }
          
          setAuthState({
            isAuthenticated: true,
            user,
            token,
            loading: false,
            error: null,
          });
        } catch (error) {
          // Token might be invalid or expired
          localStorage.removeItem('token');
          setAuthState({
            ...initialAuthState,
            loading: false,
          });
        }
      } else {
        setAuthState({
          ...initialAuthState,
          loading: false,
        });
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (username: string, password: string) => {
    setAuthState({
      ...authState,
      loading: true,
      error: null,
    });

    try {
      const response = await apiLogin(username, password);
      const { access_token, user_info } = response;

      // Check if user has 'master' role
      if (user_info.cargo !== 'master') {
        setAuthState({
          ...authState,
          loading: false,
          error: 'Usuário não tem privilégios suficientes para acessar o sistema. Consulte o administrador',
        });
        return;
      }

      // Save token to localStorage
      localStorage.setItem('token', access_token);

      setAuthState({
        isAuthenticated: true,
        user: user_info,
        token: access_token,
        loading: false,
        error: null,
      });
    } catch (error) {
      setAuthState({
        ...authState,
        loading: false,
        error: 'Usuário ou senha inválidos',
      });
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    setAuthState({
      ...initialAuthState,
      loading: false,
    });
  };

  return (
    <AuthContext.Provider value={{ authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
